import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Check, Home, Heart, Building2, Package } from "lucide-react";
import SEOHelmet from "@/components/SEOHelmet";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ContactCTASection from "@/components/ContactCTASection";
import TestimonialsSection from "@/components/TestimonialsSection";
import { useState } from "react";
import EstimateDialog from "@/components/EstimateDialog";

const Organizing = () => {
  const [showEstimateDialog, setShowEstimateDialog] = useState(false);

  const services = {
    residential: {
      icon: Home,
      title: "Residential Organizing",
      description: "Transform your Michigan home with custom organizing systems designed for how you live.",
      features: [
        "Closet & wardrobe organization systems",
        "Kitchen & pantry optimization",
        "Whole-home decluttering & flow",
        "Bedroom & bathroom organization",
        "Garage & basement solutions",
        "Move-in/move-out assistance"
      ],
      pricing: "Starting at $95/hour | Packages from $475"
    },
    commercial: {
      icon: Building2,
      title: "Commercial Organizing",
      description: "Streamline your Michigan office or retail space for maximum productivity and professional appeal.",
      features: [
        "Office workspace optimization",
        "Retail display organization",
        "Filing & document systems",
        "Storage room solutions",
        "Inventory management setup",
        "Real estate staging for commercial properties"
      ],
      pricing: "Starting at $125/hour | Custom quotes available"
    },
    adhd: {
      icon: Heart,
      title: "ADHD-Friendly Systems",
      description: "Specialized organizing for neurodivergent individuals—simple, visual, low-overwhelm solutions that last.",
      features: [
        "Visual organization systems",
        "Low-maintenance layouts",
        "Time management tools",
        "Simplified decluttering process",
        "Sustainable, judgment-free approach",
        "Personalized systems that work with your brain"
      ],
      pricing: "Starting at $95/hour | Compassionate consultation included"
    },
    realestate: {
      icon: Package,
      title: "Real Estate Prep & Downsizing",
      description: "Market-ready transformations that help Michigan homes sell faster and for more.",
      features: [
        "Pre-listing decluttering & staging",
        "Downsizing consultation & planning",
        "Estate cleanout services",
        "Strategic furniture placement",
        "Depersonalization for showings",
        "Move management & coordination"
      ],
      pricing: "Starting at $550 | 1-Week Market Ready packages available"
    }
  };

  return (
    <>
      <SEOHelmet
        title="Professional Organizer Southwest Michigan | Home Organization Near Me"
        description="Top-rated professional organizer in Southwest Michigan. Home organization, decluttering, ADHD-friendly systems, real estate staging. Serving St. Joseph, Niles, Buchanan, Benton Harbor. Free consultation."
        keywords="professional organizer near me, home organization Southwest Michigan, decluttering services Berrien County, ADHD organizing Michigan, closet organizer St Joseph MI, home organizer Niles Michigan, real estate staging Benton Harbor, professional organizer Buchanan MI, organizing services near me, best home organizer Michigan"
        canonicalUrl="https://organizebydesignmichigan.com/organizing"
      />

      <Header onBookingClick={() => setShowEstimateDialog(true)} />

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-background via-accent/10 to-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
              Professional Organizing Services in Michigan
            </h1>
            <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
              Transform your space with expert organizing systems designed for clarity, calm, and lasting results—tailored for Michigan homes and businesses.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button onClick={() => setShowEstimateDialog(true)} size="lg" className="text-lg px-8">
                Get Free Consultation
              </Button>
              <Button variant="outline" size="lg" className="text-lg px-8" asChild>
                <a href="tel:+16167270343">Call 616 727-0343</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Services Tabs */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <Tabs defaultValue="residential" className="max-w-6xl mx-auto">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4 h-auto mb-12">
              <TabsTrigger value="residential" className="py-3">Residential</TabsTrigger>
              <TabsTrigger value="commercial" className="py-3">Commercial</TabsTrigger>
              <TabsTrigger value="adhd" className="py-3">ADHD Systems</TabsTrigger>
              <TabsTrigger value="realestate" className="py-3">Real Estate Prep</TabsTrigger>
            </TabsList>

            {Object.entries(services).map(([key, service]) => {
              const IconComponent = service.icon;
              return (
                <TabsContent key={key} value={key} className="mt-8">
                  <Card className="border-border">
                    <CardHeader className="text-center pb-8">
                      <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                        <IconComponent className="h-8 w-8 text-primary" />
                      </div>
                      <CardTitle className="text-3xl font-serif font-medium mb-3">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-lg">
                        {service.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-8">
                      <div>
                        <h3 className="text-xl font-semibold mb-4">What's Included:</h3>
                        <div className="grid md:grid-cols-2 gap-3">
                          {service.features.map((feature, idx) => (
                            <div key={idx} className="flex items-start gap-2">
                              <Check className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="bg-accent/20 p-6 rounded-lg">
                        <p className="text-lg font-semibold mb-2">Investment:</p>
                        <p className="text-muted-foreground">{service.pricing}</p>
                      </div>

                      <div className="flex flex-wrap gap-4 justify-center pt-4">
                        <Button onClick={() => setShowEstimateDialog(true)} size="lg" className="px-8">
                          Book This Service
                        </Button>
                        <Button variant="outline" size="lg" className="px-8" asChild>
                          <a href="#contact">Learn More</a>
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>

      <TestimonialsSection />
      <ContactCTASection onBookingClick={() => setShowEstimateDialog(true)} />
      <Footer onBookingClick={() => setShowEstimateDialog(true)} />
      
      <EstimateDialog 
        open={showEstimateDialog} 
        onOpenChange={setShowEstimateDialog}
      />
    </>
  );
};

export default Organizing;
